import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LuMail, LuLock } from 'react-icons/lu';
import api from '../services/api';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1e3a5f 0%, #2c5282 50%, #1e3a5f 100%);
  padding: 20px;
`;

const LoginCard = styled.div`
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 48px 40px;
  width: 100%;
  max-width: 420px;
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const LogoCircle = styled.div`
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

const Logo = styled.div`
  text-align: center;
  
  h1 {
    font-size: 24px;
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 6px;
    letter-spacing: -0.5px;
  }
  
  p {
    color: #718096;
    font-size: 15px;
    font-weight: 500;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  letter-spacing: 0.2px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.span`
  position: absolute;
  left: 16px;
  color: #a0aec0;
  display: flex;
  align-items: center;
  pointer-events: none;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 14px 16px 14px 46px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  color: #2d3748;
  transition: all 0.2s ease;
  background: white;
  
  &::placeholder {
    color: #a0aec0;
  }
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    background: #f7fafc;
  }
  
  &:disabled {
    background: #f7fafc;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  padding: 16px;
  background: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 8px;
  box-shadow: 0 4px 12px rgba(66, 153, 225, 0.3);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(66, 153, 225, 0.4);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    background: #cbd5e0;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const BackLink = styled.a`
  text-align: center;
  margin-top: 24px;
  display: block;
  color: #4299e1;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
    color: #3182ce;
  }
`;

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !senha) {
      toast.error('Preencha todos os campos');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/admin/auth/login', {
        email,
        senha
      });

      // Salvar tokens no localStorage
      localStorage.setItem('admin_access_token', response.data.accessToken);
      localStorage.setItem('admin_refresh_token', response.data.refreshToken);
      localStorage.setItem('admin_user', JSON.stringify(response.data.usuario));

      toast.success('Login realizado com sucesso!');
      navigate('/admin/cupons');
    } catch (error) {
      console.error('Erro ao fazer login:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao fazer login';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LogoContainer>
          <LogoCircle>
            <img src="/logoscc.png" alt="Santa Casa Copacabana" />
          </LogoCircle>
          
          <Logo>
            <h1>Santa Casa Copacabana</h1>
            <p>Área Administrativa</p>
          </Logo>
        </LogoContainer>
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Email</Label>
            <InputWrapper>
              <InputIcon>
                <LuMail />
              </InputIcon>
              <Input
                type="email"
                placeholder="Digite seu email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </InputWrapper>
          </FormGroup>
          
          <FormGroup>
            <Label>Senha</Label>
            <InputWrapper>
              <InputIcon>
                <LuLock />
              </InputIcon>
              <Input
                type="password"
                placeholder="Digite sua senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                disabled={loading}
              />
            </InputWrapper>
          </FormGroup>
          
          <SubmitButton type="submit" disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </SubmitButton>
        </Form>
        
        {/* <BackLink onClick={() => navigate('/')}>
          ← Voltar para o portal
        </BackLink> */}
      </LoginCard>
    </LoginContainer>
  );
};

export default AdminLogin;

